import { useSyncExternalStore } from 'react';
import type { CrmData, Prestation, Facture, Client, Devis, DevisLigne } from './types';
import { supabase } from '@/integrations/supabase/client';
import { demoFilterValue, withDemoFlag } from './demoMode';
import { setDocumentBrand } from './contract/branding';

const DEFAULT_DATA: CrmData = {
  version: 3,
  exportDate: new Date().toISOString(),
  prest: [],
  options: ['Option A', 'Option B', 'Option C', 'Formule Solo', 'Formule Duo', 'Formule Trio', 'Pack Standard', 'Pack Premium'],
  services: [
    'Animation', 'Arbre de Noël', 'Baby Sitter', 'Co-cli-co', 'Cérémonie Laïque', 'DJ',
    'Décoration', 'Décorations', 'Echassier', 'Feu & Pyro', 'Feu de jour', 'Kermesse',
    'LED', 'Location de matériels', 'Mascottes', 'Media', 'Officiant de cérémonie',
    'Pack Sérénité Cérémonie', 'Photobooth', 'Photographie', 'Pâtisserie', 'Saxophoniste',
    'Sculpture sur ballon', 'Show Feu', 'Sono & Lumières', 'Traiteur', 'Vidéo', 'Violon',
    'Weddingplanner de A à Z', 'Weddingplanner Jour J', 'WP A à Z', 'WP Jour J',
  ],
  domaines: [
    'Animation', 'Ateliers enfants', 'Co-cli-co', 'Cérémonie Laïque', 'Danse', 'DJ',
    'Décoration', 'Echassier', 'Fanfare', 'Feu & Pyro', 'LED', 'Magicien', 'Mascottes',
    'Officiant de cérémonie', 'Photobooth', 'Photographie', 'Saxophoniste', 'Sculpture sur ballon',
    'Sono & Lumières', 'Traiteur', 'Vidéo', 'Violon', 'WP A à Z', 'WP Jour J',
  ],
  artistes: [],
  themes: [
    'Mariage', 'Arbre de Noël', 'Anniversaire', 'Baptême', 'Communion', 'EVJF / EVG',
    'Gala', 'Inauguration', 'Lancement de produit', 'Séminaire', 'Team building',
    'Soirée d\'entreprise', 'Cocktail', 'Vœux', 'Festival', 'Fête de village',
    'Fête de fin d\'année', 'Saint-Valentin', 'Halloween', 'Carnaval',
  ],
  paiementModes: ['Virement bancaire', 'Chèque', 'Espèces', 'CB / Stripe', 'PayPal', 'À définir'],
  relanceNotes: {},
  relanceHistory: {},
  factures: [],
  emetteur: {
    raisonSociale: 'CYGNATURE PRODUCTION',
    adresse: '',
    siret: '89847396200014',
    tvaIntra: '',
    ape: '',
    rcs: '',
    capital: '',
    email: 'contact@cygnatureproduction.fr',
    tel: '06 71 35 15 50',
    iban: '',
    bic: '',
    logoUrl: '',
    tamponUrl: '',
    prefixFacture: `F-${new Date().getFullYear()}-`,
    prochainNumero: 1,
    prefixDevis: `P-${new Date().getFullYear()}-`,
    prochainNumeroDevis: 1,
  },
  clients: [],
  devis: [],
};

let state: CrmData = { ...DEFAULT_DATA };
let loaded = false;
const listeners = new Set<() => void>();

function notify() { listeners.forEach((l) => l()); }

// ============ MAPPERS DB ↔ APP ============
function rowToPrest(r: any): Prestation {
  return {
    id: r.numero || r.id,
    structure: r.structure || 'cygnature',
    clientId: r.client_id || null,
    client: r.client_nom || '',
    tel: r.client_tel || '',
    email: r.client_email || '',
    statut: r.statut || '',
    dateEvent: r.date_event || '',
    lieu: r.lieu || '',
    km: r.km != null ? Number(r.km) : null,
    duree: r.duree || '',
    libelle: r.libelle || '',
    demande: r.demande || '',
    services: r.services || [],
    domaines: r.domaines || [],
    themes: r.themes || [],
    artistes: [],
    propTTC: r.prop_ttc != null ? Number(r.prop_ttc) : null,
    propNote: r.prop_note || '',
    devis: r.devis != null ? Number(r.devis) : null,
    acomptePct: r.acompte_pct != null ? Number(r.acompte_pct) : null,
    contratClient: r.contrat_client || '',
    contratArtiste: r.contrat_artiste || '',
    remarques: r.remarques || '',
    factureEmise: r.facture_emise || '',
    paye: r.paye || '',
    gcalId: r.gcal_id || null,
    dateCreation: r.created_at || '',
    updatedAt: r.updated_at || '',
    archivedAt: r.archived_at || null,
    canal: (r.canal as any) || '',
    priorite: (r.priorite as any) || '',
  };
}

function prestToRow(p: Prestation): any {
  return {
    numero: p.id,
    structure: p.structure || 'cygnature',
    client_nom: p.client || null,
    client_tel: p.tel || null,
    client_email: p.email || null,
    statut: p.statut || 'Demande de Rdv',
    date_event: p.dateEvent || null,
    lieu: p.lieu || null,
    km: p.km,
    duree: p.duree || null,
    libelle: p.libelle || null,
    demande: p.demande || null,
    services: p.services || [],
    domaines: p.domaines || [],
    themes: p.themes || [],
    prop_ttc: p.propTTC,
    prop_note: p.propNote || null,
    devis: p.devis,
    acompte_pct: p.acomptePct,
    contrat_client: p.contratClient || null,
    contrat_artiste: p.contratArtiste || null,
    remarques: p.remarques || null,
    facture_emise: p.factureEmise || null,
    paye: p.paye || null,
    gcal_id: p.gcalId || null,
    canal: p.canal || null,
    priorite: p.priorite || null,
  };
}

function rowToFacture(r: any, lignes: any[] = []): Facture {
  return {
    id: r.id,
    numero: r.numero,
    structure: r.structure || 'cygnature',
    type: r.type || 'Facture',
    prestationId: r.prestation_id,
    clientNom: r.client_nom || '',
    clientAdresse: r.client_adresse || '',
    clientEmail: r.client_email || '',
    clientTel: r.client_tel || '',
    clientSiret: r.client_siret || '',
    clientTvaIntra: r.client_tva_intra || '',
    dateEmission: r.date_emission || '',
    dateEcheance: r.date_echeance || '',
    datePrestation: r.date_prestation || '',
    lignes: lignes.map((l) => ({
      description: l.description,
      quantite: Number(l.quantite),
      prixUnitaireHT: Number(l.prix_unitaire_ht),
      tvaPct: Number(l.tva_pct),
      reference: l.reference ?? null,
      unite: l.unite ?? null,
      remisePct: Number(l.remise_pct || 0),
      prixBrutHT: l.prix_brut_ht == null ? null : Number(l.prix_brut_ht),
      typeLigne: l.type_ligne || 'article',
    })),
    remisePct: Number(r.remise_pct || 0),
    acompteVerse: Number(r.acompte_verse || 0),
    modePaiement: r.mode_paiement || '',
    conditionsPaiement: r.conditions_paiement || '',
    mentionsLegales: r.mentions_legales || '',
    notes: r.notes || '',
    statut: r.statut,
    datePaiement: r.date_paiement || '',
    montantPaye: Number(r.montant_paye || 0),
    factorXGenere: !!r.format_facturx,
    chorusProTransmis: !!r.chorus_pro_transmis,
    pdpTransmis: !!r.pdp_transmis,
    clientPlateformeAgree: r.client_plateforme_agree || '',
    dateCreation: r.created_at || '',
    devisId: r.devis_id || null,
    prefixDevisOrigine: r.prefix_devis_origine || null,
    echeancier: Array.isArray(r.echeancier) ? r.echeancier : undefined,
    avoirOrigineId: r.avoir_origine_id || null,
    finaliseeAt: r.finalisee_at || null,
  };
}

function rowToClient(r: any): Client {
  return {
    id: r.id,
    typeClient: r.type_client || 'particulier',
    nom: r.nom || '',
    prenom: r.prenom || '',
    societe: r.societe || '',
    fonction: r.fonction || '',
    email: r.email || '',
    tel: r.tel || '',
    telSecondaire: r.tel_secondaire || '',
    adresse: r.adresse || '',
    cp: r.cp || '',
    ville: r.ville || '',
    departement: r.departement || '',
    pays: r.pays || 'France',
    siret: r.siret || '',
    tvaIntra: r.tva_intra || '',
    source: r.source || '',
    segment: r.segment || '',
    tags: r.tags || [],
    potentielBusiness: r.potentiel_business || '',
    notes: r.notes || '',
    createdAt: r.created_at || '',
  };
}
function clientToRow(c: Client): any {
  return {
    type_client: c.typeClient,
    nom: c.nom,
    prenom: c.prenom || null,
    societe: c.societe || null,
    fonction: c.fonction || null,
    email: c.email || null,
    tel: c.tel || null,
    tel_secondaire: c.telSecondaire || null,
    adresse: c.adresse || null,
    cp: c.cp || null,
    ville: c.ville || null,
    departement: c.departement || null,
    pays: c.pays || 'France',
    siret: c.siret || null,
    tva_intra: c.tvaIntra || null,
    source: c.source || null,
    segment: c.segment || null,
    tags: c.tags || [],
    potentiel_business: c.potentielBusiness || null,
    notes: c.notes || null,
  };
}

function rowToDevis(r: any, lignes: any[] = []): Devis {
  return {
    id: r.id,
    numero: r.numero,
    structure: r.structure || 'cygnature',
    statut: r.statut,
    prestationId: r.prestation_id,
    clientId: r.client_id,
    clientNom: r.client_nom || '',
    clientAdresse: r.client_adresse || '',
    clientEmail: r.client_email || '',
    clientTel: r.client_tel || '',
    clientSiret: r.client_siret || '',
    clientTvaIntra: r.client_tva_intra || '',
    objet: r.objet || '',
    lieu: r.lieu || '',
    dateEvent: r.date_event || '',
    noteIntro: r.note_intro || '',
    conditions: r.conditions || '',
    dateEmission: r.date_emission,
    dateValidite: r.date_validite,
    acomptePct: Number(r.acompte_pct || 30),
    modePaiement: r.mode_paiement || 'Virement bancaire',
    remisePct: Number(r.remise_pct || 0),
    factureId: r.facture_id,
    convertiLe: r.converti_le,
    dateAcceptation: r.date_acceptation,
    lignes: lignes.map((l) => ({
      id: l.id, ordre: l.ordre, titre: l.titre || '', description: l.description,
      quantite: Number(l.quantite), prixUnitaireHT: Number(l.prix_unitaire_ht), tvaPct: Number(l.tva_pct),
    })),
    createdAt: r.created_at,
  };
}

// ============ LOAD FROM SUPABASE ============
async function loadAll() {
  try {
    const demo = demoFilterValue();
    const [prestRes, artistesRes, facturesRes, lignesRes, emetteursRes, clientsRes, devisRes, devisLignesRes, prestArtRes, referentielsRes, produitsRes] = await Promise.all([
      supabase.from('prestations').select('*').is('deleted_at', null).eq('is_demo', demo).order('date_event', { ascending: true }).limit(2000),
      supabase.from('artistes').select('nom_scene').is('deleted_at', null).order('nom_scene'),
      supabase.from('factures').select('*').is('deleted_at', null).eq('is_demo', demo).order('date_emission', { ascending: false }),
      supabase.from('facture_lignes').select('*').order('ordre'),
      supabase.from('emetteur').select('*'),
      supabase.from('clients').select('*').is('deleted_at', null).eq('is_demo', demo).order('nom'),
      (supabase.from('devis' as any) as any).select('*').is('deleted_at', null).eq('is_demo', demo).order('date_emission', { ascending: false }),
      (supabase.from('devis_lignes' as any) as any).select('*').order('ordre'),
      supabase.from('prestations_artistes').select('prestation_id, artiste_nom, cout, prop_ttc, ordre').eq('inclus', true).order('ordre'),
      supabase.from('referentiels').select('type, valeur, ordre, actif').eq('actif', true).order('valeur'),
      (supabase.from('produits' as any) as any).select('*').order('categorie').order('ordre').order('libelle'),
    ]);

    // Index artistes + cumul coût et prop TTC par prestation_id (UUID DB)
    const artistesByPrestId = new Map<string, string[]>();
    const coutByPrestId = new Map<string, number>();
    const propTtcByPrestId = new Map<string, number>();
    const detailsByPrestId = new Map<string, { nom: string; cout: number | null; propTtc: number | null }[]>();
    (prestArtRes.data || []).forEach((row: any) => {
      if (row.artiste_nom) {
        const arr = artistesByPrestId.get(row.prestation_id) || [];
        arr.push(row.artiste_nom);
        artistesByPrestId.set(row.prestation_id, arr);
        const det = detailsByPrestId.get(row.prestation_id) || [];
        det.push({ nom: row.artiste_nom, cout: row.cout != null ? Number(row.cout) : null, propTtc: row.prop_ttc != null ? Number(row.prop_ttc) : null });
        detailsByPrestId.set(row.prestation_id, det);
      }
      if (row.cout != null) {
        coutByPrestId.set(row.prestation_id, (coutByPrestId.get(row.prestation_id) || 0) + Number(row.cout));
      }
      if (row.prop_ttc != null) {
        propTtcByPrestId.set(row.prestation_id, (propTtcByPrestId.get(row.prestation_id) || 0) + Number(row.prop_ttc));
      }
    });

    const prest = (prestRes.data || []).map((r: any) => {
      const p = rowToPrest(r);
      p.artistes = artistesByPrestId.get(r.id) || [];
      p.coutArtistes = coutByPrestId.has(r.id) ? coutByPrestId.get(r.id)! : null;
      p.artistesDetails = detailsByPrestId.get(r.id) || [];
      // Prop. TTC = somme des prop_ttc (CY) par artiste si présents, sinon valeur saisie sur la prestation
      if (propTtcByPrestId.has(r.id)) {
        p.propTTC = propTtcByPrestId.get(r.id)!;
      }
      return p;
    });
    const artistes = (artistesRes.data || []).map((a: any) => a.nom_scene);
    const lignesByFact = new Map<string, any[]>();
    (lignesRes.data || []).forEach((l: any) => {
      const arr = lignesByFact.get(l.facture_id) || [];
      arr.push(l);
      lignesByFact.set(l.facture_id, arr);
    });
    const factures = (facturesRes.data || []).map((f: any) => rowToFacture(f, lignesByFact.get(f.id) || []));
    const clients = (clientsRes.data || []).map(rowToClient);

    const lignesByDevis = new Map<string, any[]>();
    (devisLignesRes.data || []).forEach((l: any) => {
      const arr = lignesByDevis.get(l.devis_id) || [];
      arr.push(l);
      lignesByDevis.set(l.devis_id, arr);
    });
    const devis = (devisRes.data || []).map((d: any) => rowToDevis(d, lignesByDevis.get(d.id) || []));

    // ===== Émetteurs (multi-structures) =====
    const mapEmetteur = (e: any) => ({
      raisonSociale: e.raison_sociale || '',
      adresse: e.adresse || '',
      siret: e.siret || '',
      tvaIntra: e.tva_intra || '',
      ape: e.ape || '',
      rcs: e.rcs || '',
      capital: e.capital || '',
      email: e.email || '',
      tel: e.tel || '',
      iban: e.iban || '',
      bic: e.bic || '',
      logoUrl: e.logo_url || '',
      tamponUrl: e.tampon_url || '',
      prefixFacture: e.prefix_facture || `F-${new Date().getFullYear()}-`,
      prochainNumero: e.prochain_numero || 1,
      prefixDevis: e.prefix_devis || `P-${new Date().getFullYear()}-`,
      prochainNumeroDevis: e.prochain_numero_devis || 1,
      plateformeAgree: e.plateforme_agree || '',
    });
    const emetteursList = (emetteursRes.data || []) as any[];
    const emetteurs: Record<string, any> = {};
    emetteursList.forEach((e) => {
      const code = e.code || 'cygnature';
      emetteurs[code] = mapEmetteur(e);
    });
    // Fallback : si pas de SVR créé, on duplique cygnature
    if (!emetteurs.cygnature && emetteursList[0]) emetteurs.cygnature = mapEmetteur(emetteursList[0]);
    if (!emetteurs.suivez_vos_reves) emetteurs.suivez_vos_reves = emetteurs.cygnature || state.emetteur;
    const emetteur = emetteurs.cygnature || state.emetteur;
    // Logo et tampon personnalisés : enregistrés côté store pour les rendus de documents
    (['cygnature', 'suivez_vos_reves'] as const).forEach((s) =>
      setDocumentBrand(s, { logo: emetteurs[s]?.logoUrl, tampon: emetteurs[s]?.tamponUrl }),
    );

    // Référentiels dynamiques (services, domaines, thèmes, options, modes de paiement)
    const refByType: Record<string, string[]> = { service: [], domaine: [], theme: [], option: [], paiement_mode: [], produit_categorie: [] };
    ((referentielsRes as any).data || []).forEach((r: any) => {
      if (refByType[r.type]) refByType[r.type].push(r.valeur);
    });
    const refOverrides: Partial<CrmData> = {};
    if (refByType.service.length) refOverrides.services = refByType.service;
    if (refByType.domaine.length) refOverrides.domaines = refByType.domaine;
    if (refByType.theme.length) refOverrides.themes = refByType.theme;
    if (refByType.option.length) refOverrides.options = refByType.option;
    if (refByType.paiement_mode.length) refOverrides.paiementModes = refByType.paiement_mode;

    // Catalogue produits (décorations, locations, prestations de services…)
    const produits = ((produitsRes as any).data || []).map((r: any) => ({
      id: r.id,
      categorie: r.categorie || '',
      libelle: r.libelle || '',
      description: r.description || '',
      coutHT: Number(r.cout_ht || 0),
      prixHT: Number(r.prix_ht || 0),
      unite: r.unite || '',
      actif: r.actif !== false,
      ordre: Number(r.ordre || 0),
      reference: r.reference || '',
      sousFamille: r.sous_famille || '',
      tvaPct: r.tva_pct == null ? 20 : Number(r.tva_pct),
      fournisseur: r.fournisseur || '',
    }));
    const produitCategories = Array.from(
      new Set([...refByType.produit_categorie, ...produits.map((p: any) => p.categorie).filter(Boolean)])
    ).sort((a, b) => a.localeCompare(b, 'fr'));

    state = { ...state, prest, artistes, factures, emetteur, emetteurs: emetteurs as any, clients, devis, produits, produitCategories, ...refOverrides };
    loaded = true;
    notify();
  } catch (e) {
    console.error('[crmStore] loadAll error', e);
  }
}

// Recharge à chaque changement de session (login/logout)
supabase.auth.onAuthStateChange(() => { loadAll(); });
// Recharge quand on bascule entre mode réel et mode démo
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === 'crm-demo-mode') loadAll(); });
  // Listener interne (même onglet)
  (window as any).__crmReloadOnDemoChange = () => loadAll();
}
// Chargement initial systématique (auth temporaire = gate mot de passe,
// les politiques RLS publiques temporaires autorisent la lecture)
loadAll();

type RefTypeCode = 'service' | 'domaine' | 'theme' | 'option' | 'paiement_mode' | 'produit_categorie';

export const crmStore = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  get(): CrmData { return state; },
  set(next: CrmData) { state = next; notify(); },
  update(partial: Partial<CrmData>) { state = { ...state, ...partial }; notify(); },
  reload: loadAll,
  isLoaded: () => loaded,

  async addReferentiel(type: RefTypeCode, valeur: string) {
    const v = valeur.trim();
    if (!v) return;
    const { error } = await (supabase.from('referentiels') as any).upsert({ type, valeur: v, actif: true, ordre: 0 }, { onConflict: 'type,valeur' });
    if (error) throw error;
    await loadAll();
  },
  async removeReferentiel(type: RefTypeCode, valeur: string) {
    const { error } = await supabase.from('referentiels').delete().eq('type', type).eq('valeur', valeur);
    if (error) throw error;
    await loadAll();
  },
  async renameReferentiel(type: RefTypeCode, oldValeur: string, newValeur: string) {
    const v = newValeur.trim();
    if (!v || v === oldValeur) return;
    const { error } = await (supabase.from('referentiels') as any).update({ valeur: v }).eq('type', type).eq('valeur', oldValeur);
    if (error) throw error;
    await loadAll();
  },

  // ===== Catalogue produits =====
  async saveProduit(p: { id?: string; categorie: string; libelle: string; description?: string; coutHT: number; prixHT: number; unite?: string; actif?: boolean; ordre?: number; reference?: string; sousFamille?: string; tvaPct?: number; fournisseur?: string }) {
    const row: any = {
      categorie: p.categorie || null,
      libelle: p.libelle.trim(),
      description: p.description || null,
      cout_ht: Number(p.coutHT || 0),
      prix_ht: Number(p.prixHT || 0),
      unite: p.unite || null,
      actif: p.actif !== false,
      ordre: Number(p.ordre || 0),
      reference: p.reference || null,
      sous_famille: p.sousFamille || null,
      tva_pct: p.tvaPct ?? 20,
      fournisseur: p.fournisseur || null,
    };
    if (p.id) {
      const { error } = await (supabase.from('produits' as any) as any).update(row).eq('id', p.id);
      if (error) throw error;
    } else {
      const { error } = await (supabase.from('produits' as any) as any).insert(row);
      if (error) throw error;
    }
    await loadAll();
  },
  async deleteProduit(id: string) {
    const { error } = await (supabase.from('produits' as any) as any).delete().eq('id', id);
    if (error) throw error;
    await loadAll();
  },

  async upsertPrest(p: Prestation) {
    const row = prestToRow(p);
    const demo = demoFilterValue();
    const nowIso = new Date().toISOString();
    let prestId: string | null = null;
    // Cherche d'abord par numero, sinon par UUID (id local peut être l'un ou l'autre)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id);
    let existing: any = null;
    const { data: byNum } = await supabase.from('prestations').select('id').eq('numero', p.id).eq('is_demo', demo).maybeSingle();
    existing = byNum;
    if (!existing && isUuid) {
      const { data: byId } = await supabase.from('prestations').select('id').eq('id', p.id).eq('is_demo', demo).maybeSingle();
      existing = byId;
    }
    if (existing) {
      const { error } = await supabase.from('prestations').update(row).eq('id', existing.id);
      if (error) throw error;
      prestId = (existing as any).id;
    } else {
      const { data: inserted, error } = await supabase.from('prestations').insert(withDemoFlag(row)).select('id').single();
      if (error) throw error;
      prestId = (inserted as any)?.id || null;
    }

    // Synchronise les artistes liés (table prestations_artistes)
    if (prestId) {
      // Récupère les artistes existants pour ne supprimer/insérer que le delta
      const { data: existingLinks } = await supabase
        .from('prestations_artistes')
        .select('id, artiste_nom, artiste_id')
        .eq('prestation_id', prestId)
        .eq('is_demo', demo);

      const currentNames = new Set((p.artistes || []).map((n) => n.trim()).filter(Boolean));
      const existingByName = new Map<string, any>();
      (existingLinks || []).forEach((l: any) => existingByName.set((l.artiste_nom || '').trim(), l));

      // Supprime les liens qui ne sont plus présents
      const toDelete = (existingLinks || []).filter((l: any) => !currentNames.has((l.artiste_nom || '').trim()));
      if (toDelete.length) {
        await supabase.from('prestations_artistes').delete().in('id', toDelete.map((l: any) => l.id));
      }

      // Ajoute les nouveaux artistes
      const toInsert: any[] = [];
      let ordre = 0;
      for (const name of currentNames) {
        if (existingByName.has(name)) { ordre++; continue; }
        // Tente de retrouver l'artiste_id par nom de scène
        const { data: artisteRow } = await supabase
          .from('artistes')
          .select('id')
          .eq('nom_scene', name)
          .maybeSingle();
        toInsert.push({
          prestation_id: prestId,
          artiste_id: (artisteRow as any)?.id || null,
          artiste_nom: name,
          ordre: ordre++,
          statut: 'retenu',
        });
      }
      if (toInsert.length) {
        await supabase.from('prestations_artistes').insert(toInsert.map((r) => withDemoFlag(r)));
      }
    }

    // Optimistic local update
    const updatedPrest: Prestation = { ...p, dateCreation: p.dateCreation || nowIso, updatedAt: nowIso };
    const idx = state.prest.findIndex((x) => x.id === p.id);
    const list = [...state.prest];
    if (idx >= 0) list[idx] = updatedPrest; else list.push(updatedPrest);
    state = { ...state, prest: list };
    notify();
    return prestId;
  },

  async removePrest(id: string) {
    const demo = demoFilterValue();
    const nowIso = new Date().toISOString();
    // L'id local peut être soit le numéro soit l'UUID DB (si numero null)
    const { data: upd1 } = await supabase.from('prestations').update({ deleted_at: nowIso }).eq('numero', id).eq('is_demo', demo).select('id');
    if (!upd1 || upd1.length === 0) {
      await supabase.from('prestations').update({ deleted_at: nowIso }).eq('id', id).eq('is_demo', demo);
    }
    state = { ...state, prest: state.prest.filter((p) => p.id !== id) };
    notify();
  },

  async archivePrest(id: string) {
    const demo = demoFilterValue();
    const nowIso = new Date().toISOString();
    const { data: upd1 } = await supabase.from('prestations').update({ archived_at: nowIso }).eq('numero', id).eq('is_demo', demo).select('id');
    if (!upd1 || upd1.length === 0) {
      await supabase.from('prestations').update({ archived_at: nowIso }).eq('id', id).eq('is_demo', demo);
    }
    const list = state.prest.map((p) => p.id === id ? { ...p, archivedAt: nowIso } : p);
    state = { ...state, prest: list };
    notify();
  },

  async restorePrest(id: string) {
    const demo = demoFilterValue();
    const { data: upd1 } = await supabase.from('prestations').update({ archived_at: null }).eq('numero', id).eq('is_demo', demo).select('id');
    if (!upd1 || upd1.length === 0) {
      await supabase.from('prestations').update({ archived_at: null }).eq('id', id).eq('is_demo', demo);
    }
    const list = state.prest.map((p) => p.id === id ? { ...p, archivedAt: null } : p);
    state = { ...state, prest: list };
    notify();
  },

  async runAutoArchive() {
    const { data, error } = await (supabase as any).rpc('auto_archive_prestations');
    if (error) throw error;
    await loadAll();
    return data?.[0] || { realisees: 0, expirees: 0, refusees: 0, brouillons_abandonnes: 0 };
  },

  importJson(json: CrmData) {
    state = { ...DEFAULT_DATA, ...json };
    notify();
  },

  reset() {
    state = { ...DEFAULT_DATA };
    notify();
  },

  resetToSeed() { /* no-op : la base de données est maintenant la source de vérité */ },

  async upsertFacture(f: Facture) {
    // Résout l'UUID réel de la prestation à partir de son numéro (Prestation.id = numero côté UI)
    let prestationUuid: string | null = null;
    if (f.prestationId) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(f.prestationId);
      if (isUuid) {
        prestationUuid = f.prestationId;
      } else {
        const { data: pr } = await supabase
          .from('prestations')
          .select('id')
          .eq('numero', f.prestationId)
          .eq('is_demo', demoFilterValue())
          .maybeSingle();
        prestationUuid = pr?.id || null;
      }
    }
    const row = {
      numero: f.numero,
      structure: f.structure || 'cygnature',
      type: f.type,
      prestation_id: prestationUuid,
      client_nom: f.clientNom,
      client_adresse: f.clientAdresse || null,
      client_email: f.clientEmail || null,
      client_tel: f.clientTel || null,
      client_siret: f.clientSiret || null,
      client_tva_intra: f.clientTvaIntra || null,
      date_emission: f.dateEmission,
      date_echeance: f.dateEcheance,
      date_prestation: f.datePrestation || null,
      remise_pct: f.remisePct,
      acompte_verse: f.acompteVerse,
      mode_paiement: f.modePaiement || null,
      conditions_paiement: f.conditionsPaiement || null,
      mentions_legales: f.mentionsLegales || null,
      notes: f.notes || null,
      statut: f.statut,
      date_paiement: f.datePaiement || null,
      montant_paye: f.montantPaye,
      format_facturx: f.factorXGenere,
      chorus_pro_transmis: f.chorusProTransmis,
      pdp_transmis: f.pdpTransmis,
      client_plateforme_agree: f.clientPlateformeAgree || null,
      echeancier: f.echeancier && f.echeancier.length ? f.echeancier : null,
      avoir_origine_id: f.avoirOrigineId || null,
      finalisee_at: f.finaliseeAt || (f.statut !== 'Brouillon' ? new Date().toISOString() : null),
      deleted_at: null,
    };
    let factureDbId: string | null = null;
    const demo = demoFilterValue();
    const { data: existing, error: existErr } = await supabase
      .from('factures')
      .select('id, is_demo')
      .eq('numero', f.numero)
      .eq('is_demo', demo)
      .maybeSingle();
    if (existErr) {
      console.error('[upsertFacture] lookup error', existErr);
      throw existErr;
    }
    if (existing) {
      factureDbId = existing.id;
      const { error: updErr } = await supabase.from('factures').update(row).eq('id', existing.id);
      if (updErr) { console.error('[upsertFacture] update error', updErr); throw updErr; }
      await supabase.from('facture_lignes').delete().eq('facture_id', existing.id);
    } else {
      const { data: ins, error: insErr } = await supabase
        .from('factures')
        .insert(withDemoFlag(row))
        .select('id')
        .single();
      if (insErr || !ins) {
        console.error('[upsertFacture] insert error', insErr, 'payload:', withDemoFlag(row));
        throw insErr || new Error('Insertion facture impossible (aucune ligne retournée)');
      }
      factureDbId = (ins as any).id;
    }
    if (f.lignes.length && factureDbId) {
      const { error: ligErr } = await supabase.from('facture_lignes').insert(
        f.lignes.map((l, i) => withDemoFlag({
          facture_id: factureDbId,
          ordre: i,
          description: l.description,
          quantite: l.quantite,
          prix_unitaire_ht: l.prixUnitaireHT,
          tva_pct: l.tvaPct,
          reference: l.reference || null,
          unite: l.unite || null,
          remise_pct: l.remisePct || 0,
          prix_brut_ht: l.prixBrutHT ?? null,
          type_ligne: l.typeLigne || 'article',
        }))
      );
      if (ligErr) { console.error('[upsertFacture] lignes error', ligErr); throw ligErr; }
    }
    await loadAll();
  },

  async removeFacture(id: string) {
    await supabase.from('factures').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    state = { ...state, factures: state.factures.filter((f) => f.id !== id) };
    notify();
  },

  nextNumeroFacture(structure: 'cygnature' | 'suivez_vos_reves' = 'cygnature'): string {
    const emetteurs = (state.emetteurs || {}) as any;
    const e = emetteurs[structure] || state.emetteur;
    const num = `${e.prefixFacture}${String(e.prochainNumero).padStart(4, '0')}`;
    const newE = { ...e, prochainNumero: e.prochainNumero + 1 };
    const newEmetteurs = { ...emetteurs, [structure]: newE };
    state = { ...state, emetteurs: newEmetteurs, emetteur: structure === 'cygnature' ? newE : state.emetteur };
    notify();
    supabase.from('emetteur').select('id, code').eq('code', structure).maybeSingle().then(({ data }) => {
      if (data) supabase.from('emetteur').update({ prochain_numero: newE.prochainNumero }).eq('id', (data as any).id);
    });
    return num;
  },

  async updateEmetteur(partial: Partial<CrmData['emetteur']>, structure: 'cygnature' | 'suivez_vos_reves' = 'cygnature') {
    const emetteurs = (state.emetteurs || {}) as any;
    const current = emetteurs[structure] || state.emetteur;
    const newE = { ...current, ...partial };
    const newEmetteurs = { ...emetteurs, [structure]: newE };
    state = {
      ...state,
      emetteurs: newEmetteurs as any,
      emetteur: structure === 'cygnature' ? newE : state.emetteur,
    };
    setDocumentBrand(structure, { logo: newE.logoUrl, tampon: newE.tamponUrl });
    notify();
    const dbRow: any = {
      raison_sociale: newE.raisonSociale, adresse: newE.adresse, siret: newE.siret,
      tva_intra: newE.tvaIntra, ape: newE.ape, rcs: newE.rcs, capital: newE.capital,
      email: newE.email, tel: newE.tel, iban: newE.iban, bic: newE.bic,
      logo_url: newE.logoUrl,
      tampon_url: newE.tamponUrl || null,
      prefix_facture: newE.prefixFacture, prochain_numero: newE.prochainNumero,
      prefix_devis: newE.prefixDevis || null, prochain_numero_devis: newE.prochainNumeroDevis || 1,
      plateforme_agree: newE.plateformeAgree || null,
    };
    const { data } = await supabase.from('emetteur').select('id').eq('code', structure).maybeSingle();
    if (data) await supabase.from('emetteur').update(dbRow).eq('id', (data as any).id);
  },

  // ============ CLIENTS ============
  async upsertClient(c: Client): Promise<Client | null> {
    const row = clientToRow(c);
    if (c.id && state.clients?.find((x) => x.id === c.id)) {
      const { data } = await supabase.from('clients').update(row).eq('id', c.id).select('*').single();
      await loadAll();
      return data ? rowToClient(data) : null;
    }
    const { data } = await supabase.from('clients').insert(withDemoFlag(row)).select('*').single();
    await loadAll();
    return data ? rowToClient(data) : null;
  },

  async removeClient(id: string) {
    await supabase.from('clients').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    state = { ...state, clients: (state.clients || []).filter((c) => c.id !== id) };
    notify();
  },

  // ============ DEVIS ============
  nextNumeroDevis(structure: 'cygnature' | 'suivez_vos_reves' = 'cygnature'): string {
    const emetteurs = (state.emetteurs || {}) as any;
    const e = emetteurs[structure] || state.emetteur;
    const prefix = e.prefixDevis || `P-${new Date().getFullYear()}-`;
    const next = e.prochainNumeroDevis || 1;
    const num = `${prefix}${String(next).padStart(4, '0')}`;
    const newE = { ...e, prochainNumeroDevis: next + 1 };
    const newEmetteurs = { ...emetteurs, [structure]: newE };
    state = { ...state, emetteurs: newEmetteurs, emetteur: structure === 'cygnature' ? newE : state.emetteur };
    notify();
    supabase.from('emetteur').select('id, code').eq('code', structure).maybeSingle().then(({ data }) => {
      if (data) (supabase.from('emetteur') as any).update({ prochain_numero_devis: newE.prochainNumeroDevis }).eq('id', (data as any).id).then(() => {});
    });
    return num;
  },

  async upsertDevis(d: Devis) {
    const row: any = {
      numero: d.numero,
      structure: d.structure || 'cygnature',
      statut: d.statut,
      prestation_id: d.prestationId,
      client_id: d.clientId,
      client_nom: d.clientNom,
      client_adresse: d.clientAdresse || null,
      client_email: d.clientEmail || null,
      client_tel: d.clientTel || null,
      client_siret: d.clientSiret || null,
      client_tva_intra: d.clientTvaIntra || null,
      objet: d.objet || null,
      lieu: d.lieu || null,
      date_event: d.dateEvent || null,
      note_intro: d.noteIntro || null,
      conditions: d.conditions || null,
      date_emission: d.dateEmission,
      date_validite: d.dateValidite,
      acompte_pct: d.acomptePct,
      mode_paiement: d.modePaiement,
      remise_pct: d.remisePct,
      date_acceptation: d.dateAcceptation || null,
    };
    let devisDbId = d.id;
    const { data: existing } = await (supabase.from('devis' as any) as any).select('id').eq('numero', d.numero).maybeSingle();
    if (existing) {
      devisDbId = (existing as any).id;
      await (supabase.from('devis' as any) as any).update(row).eq('id', devisDbId);
      await (supabase.from('devis_lignes' as any) as any).delete().eq('devis_id', devisDbId);
    } else {
      const { data: ins } = await (supabase.from('devis' as any) as any).insert(withDemoFlag(row)).select('id').single();
      if (ins) devisDbId = (ins as any).id;
    }
    if (d.lignes.length && devisDbId) {
      await (supabase.from('devis_lignes' as any) as any).insert(
        d.lignes.map((l: DevisLigne, i: number) => withDemoFlag({
          devis_id: devisDbId,
          ordre: i,
          titre: l.titre || null,
          description: l.description,
          quantite: l.quantite,
          prix_unitaire_ht: l.prixUnitaireHT,
          tva_pct: l.tvaPct,
        }))
      );
    }
    await loadAll();
    return devisDbId;
  },

  async removeDevis(id: string) {
    await (supabase.from('devis' as any) as any).update({ deleted_at: new Date().toISOString() }).eq('id', id);
    state = { ...state, devis: (state.devis || []).filter((d) => d.id !== id) };
    notify();
  },

  async convertirDevisEnFacture(devisId: string): Promise<string | null> {
    const { data, error } = await (supabase.rpc as any)('convertir_devis_en_facture', { _devis_id: devisId });
    if (error) { console.error('[convertirDevis]', error); return null; }
    await loadAll();
    return data as string;
  },

  // ============ RELANCES AUTO (Propal transmise → J+3, J+7, J+15, J+30) ============
  async scheduleRelancesPropal(prestNumero: string): Promise<number> {
    // Récupère l'UUID de la prestation
    const { data: pRow } = await supabase
      .from('prestations')
      .select('id')
      .eq('numero', prestNumero)
      .maybeSingle();
    if (!pRow) return 0;
    const prestId = (pRow as any).id;

    // Vérifie si des relances commerciales existent déjà
    const { data: existingRelances } = await supabase
      .from('relances')
      .select('id')
      .eq('prestation_id', prestId)
      .eq('type', 'commerciale');
    if (existingRelances && existingRelances.length > 0) return 0;

    const today = new Date();
    const offsets = [3, 7, 15, 30];
    const rows = offsets.map((days) => {
      const d = new Date(today);
      d.setDate(d.getDate() + days);
      return {
        prestation_id: prestId,
        type: 'commerciale',
        date_relance: d.toISOString().slice(0, 10),
        note: `Relance auto J+${days} (Propal transmise)`,
        cloturee: false,
      };
    });
    const { error } = await supabase.from('relances').insert(rows.map((r) => withDemoFlag(r)));
    if (error) { console.error('[scheduleRelancesPropal]', error); return 0; }
    return rows.length;
  },

  // ============ HISTORIQUE (audit_log d'une prestation) ============
  async fetchPrestationHistory(prestNumero: string): Promise<Array<{ id: string; created_at: string; action: string; user_email: string | null; old_data: any; new_data: any }>> {
    const { data: pRow } = await supabase
      .from('prestations')
      .select('id, created_at, updated_at')
      .eq('numero', prestNumero)
      .maybeSingle();
    if (!pRow) return [];
    const { data, error } = await supabase
      .from('audit_log')
      .select('id, created_at, action, user_email, old_data, new_data')
      .eq('table_name', 'prestations')
      .eq('record_id', (pRow as any).id)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) {
      // L'utilisateur n'est probablement pas admin (audit_log RLS) — fallback sur created_at/updated_at
      return [
        { id: 'created', created_at: (pRow as any).created_at, action: 'INSERT', user_email: null, old_data: null, new_data: null },
        ...((pRow as any).updated_at !== (pRow as any).created_at
          ? [{ id: 'updated', created_at: (pRow as any).updated_at, action: 'UPDATE', user_email: null, old_data: null, new_data: null }]
          : []),
      ];
    }
    return data || [];
  },
};

export function useCrm(): CrmData {
  return useSyncExternalStore(crmStore.subscribe, crmStore.get, crmStore.get);
}

export function nextPrestId(): string {
  const nums = state.prest.map((p) => parseInt(p.id.replace(/\D/g, ''), 10) || 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return `P${max + 1}`;
}

/** Génère un ID au format YYMMDD/Nom/NNN (style Excel Cygnature) */
export function nextPrestIdForClient(clientNom: string, dateEvent?: string): string {
  const d = dateEvent ? new Date(dateEvent) : new Date();
  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const datePart = `${yy}${mm}${dd}`;
  const nom = (clientNom || 'CLIENT').toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9 \-&]/g, '').trim().replace(/\s+/g, '/').slice(0, 50) || 'CLIENT';
  const prefix = `${datePart}/${nom}/`;
  const existing = state.prest
    .filter((p) => (p.id || '').startsWith(prefix))
    .map((p) => parseInt((p.id || '').split('/')[2] || '0', 10) || 0);
  const next = (existing.length ? Math.max(...existing) : 0) + 1;
  return `${prefix}${String(next).padStart(3, '0')}`;
}

/** ============ TÂCHES (checklist par prestation) ============ */
const TACHES_TEMPLATES: Record<string, { libelle: string; categorie: string }[]> = {
  'Demande de Rdv': [
    { libelle: 'Premier contact (tel/email)', categorie: 'Commercial' },
    { libelle: 'Qualifier le besoin', categorie: 'Commercial' },
  ],
  'Propal A FAIRE': [
    { libelle: 'Calculer les coûts artistes', categorie: 'Production' },
    { libelle: 'Rédiger la proposition', categorie: 'Commercial' },
  ],
  'Propal transmise': [
    { libelle: 'Confirmer réception au client', categorie: 'Commercial' },
    { libelle: 'Programmer relance J+5', categorie: 'Commercial' },
  ],
  'Signé': [
    { libelle: 'Envoyer contrat client', categorie: 'Administratif' },
    { libelle: 'Demander acompte', categorie: 'Administratif' },
    { libelle: 'Confirmer artistes', categorie: 'Production' },
    { libelle: 'Briefing technique', categorie: 'Production' },
    { libelle: 'Préparer matériel', categorie: 'Production' },
  ],
  'Réalisée': [
    { libelle: 'Émettre facture solde', categorie: 'Administratif' },
    { libelle: 'Envoyer remerciements + demande avis', categorie: 'Commercial' },
    { libelle: 'Payer artistes', categorie: 'Administratif' },
  ],
};

export const tachesStore = {
  async list(prestationId: string) {
    const { data } = await (supabase.from('prestations_taches' as any) as any)
      .select('*').eq('prestation_id', prestationId).order('ordre');
    return (data || []) as any[];
  },
  async add(prestationId: string, libelle: string, extras: Partial<{ categorie: string; responsable: string; dateLimite: string; ordre: number }> = {}) {
    await (supabase.from('prestations_taches' as any) as any).insert(withDemoFlag({
      prestation_id: prestationId,
      libelle,
      categorie: extras.categorie || null,
      responsable: extras.responsable || null,
      date_limite: extras.dateLimite || null,
      ordre: extras.ordre ?? 0,
    }));
  },
  async toggle(id: string, fait: boolean) {
    await (supabase.from('prestations_taches' as any) as any).update({ fait }).eq('id', id);
  },
  async remove(id: string) {
    await (supabase.from('prestations_taches' as any) as any).delete().eq('id', id);
  },
  async applyTemplate(prestationId: string, statut: string) {
    const tpl = TACHES_TEMPLATES[statut];
    if (!tpl) return 0;
    const existing = await this.list(prestationId);
    const have = new Set(existing.map((t: any) => (t.libelle || '').toLowerCase()));
    const toInsert = tpl.filter((t) => !have.has(t.libelle.toLowerCase()))
      .map((t, i) => ({
        prestation_id: prestationId,
        libelle: t.libelle,
        categorie: t.categorie,
        ordre: existing.length + i,
      }));
    if (!toInsert.length) return 0;
    await (supabase.from('prestations_taches' as any) as any).insert(toInsert.map((r) => withDemoFlag(r)));
    return toInsert.length;
  },
};
